import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';

interface GlobalTimeFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  hourFormat?: '12' | '24';
}

export const GlobalTimeField: React.FC<GlobalTimeFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = 'HH:MM AM',
  required = false,
  disabled = false,
  error = '',
  icon = 'pi pi-clock',
  fullWidth = true,
  hourFormat = '12',
}) => {
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Generate common time suggestions
  const generateTimeSuggestions = (): string[] => {
    const times: string[] = [];
    
    if (hourFormat === '12') {
      for (let hour = 1; hour <= 12; hour++) {
        for (let min = 0; min < 60; min += 15) {
          const formattedMin = min.toString().padStart(2, '0');
          times.push(`${hour}:${formattedMin} AM`);
          times.push(`${hour}:${formattedMin} PM`);
        }
      }
    } else {
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += 15) {
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMin = min.toString().padStart(2, '0');
          times.push(`${formattedHour}:${formattedMin}`);
        }
      }
    }
    
    return times;
  };

  // Filter suggestions based on input
  const filterSuggestions = (inputValue: string) => {
    if (!inputValue) {
      setSuggestions(generateTimeSuggestions().slice(0, 8)); // Show first 8
      return;
    }
    
    const allTimes = generateTimeSuggestions();
    const filtered = allTimes.filter(time => 
      time.toLowerCase().includes(inputValue.toLowerCase())
    );
    setSuggestions(filtered.slice(0, 8)); // Show max 8 suggestions
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    filterSuggestions(newValue);
  };

  const handleFocus = () => {
    setFocused(true);
    setShowSuggestions(true);
    filterSuggestions(value);
  };

  const handleBlur = () => {
    setFocused(false);
    // Delay hiding to allow click on suggestion
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleSuggestionClick = (time: string) => {
    onChange(time);
    setShowSuggestions(false);
  };

  // Validate time format
  const isValidTime = (timeStr: string): boolean => {
    if (!timeStr) return true; // Empty is valid (will be caught by required)
    
    if (hourFormat === '12') {
      const regex = /^(1[0-2]|0?[1-9]):([0-5][0-9])\s?(AM|PM|am|pm)$/;
      return regex.test(timeStr);
    } else {
      const regex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
      return regex.test(timeStr);
    }
  };

  const validationError = value && !isValidTime(value) 
    ? `Invalid time format. Expected: ${hourFormat === '12' ? 'HH:MM AM/PM' : 'HH:MM'}` 
    : error;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', position: 'relative' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: validationError ? '#dc3545' : '#0f3d3e',
          marginBottom: '6px',
          letterSpacing: '0.2px'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: validationError ? '#dc3545' : '#0f3d3e',
              fontSize: '1rem',
              zIndex: 1,
              opacity: 0.5
            }}
          ></i>
        )}
        
        <InputText
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            height: '44px',
            paddingLeft: '42px',
            paddingRight: '14px',
            border: `1px solid ${validationError ? '#dc3545' : (focused ? '#0f3d3e' : '#e5e7eb')}`,
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: 500,
            background: disabled ? '#f8f9fa' : 'white',
            color: disabled ? '#999' : '#333',
            transition: 'all 0.2s ease',
            boxShadow: focused ? '0 0 0 3px rgba(15, 61, 62, 0.1)' : 'none'
          }}
        />

        {/* Time Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && !disabled && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(15, 61, 62, 0.15)',
            zIndex: 1000,
            maxHeight: '280px',
            overflowY: 'auto'
          }}>
            <div style={{ padding: '8px' }}>
              {suggestions.map((time, index) => (
                <button
                  key={index}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur
                    handleSuggestionClick(time);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#333',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(15, 61, 62, 0.08)';
                    e.currentTarget.style.color = '#0f3d3e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#333';
                  }}
                >
                  <i className="pi pi-clock" style={{ fontSize: '0.8rem', opacity: 0.6 }}></i>
                  {time}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {validationError && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#dc3545',
          fontWeight: 500
        }}>
          {validationError}
        </span>
      )}

      <style>{`
        .global-time-field-input:hover {
          border-color: #1a5555 !important;
        }

        /* Custom scrollbar for suggestions */
        div[style*="overflowY: auto"]::-webkit-scrollbar {
          width: 6px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
          background: #0f3d3e;
          border-radius: 10px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
          background: #1a5555;
        }
      `}</style>
    </div>
  );
};